import React, { useState } from "react";
import { toast } from "react-toastify";
import { CircularProgress } from "@mui/material";
import { getToken } from "../../services/Token";
import { useVotePollMutation } from "../../api/engageApi";
import PostHeader from "./PostHeader";
import {
  ANON_BADGE_CLASS,
  POLL_BADGE_CLASS,
  canVoteInPolls,
  formatPollDeadline,
  mergePollVote,
  pollPercent,
} from "./constants";

// One component, two states, chosen by the data rather than a local flag:
// myVote === null and not closed -> the ballot; anything else -> results.
//
// `onVoted` hands the merged poll block up so the owner of the list decides
// where it lives. Today that is local state on the Engage screen. When the feed
// query lands, the parent dispatches updateQueryData with this same block and
// nothing in here changes.
const PollCard = ({ post, onVoted }) => {
  const [votePoll, { isLoading }] = useVotePollMutation();
  const [pendingId, setPendingId] = useState(null);
  const poll = post.poll;
  // ADMIN and PROXY_ADMIN do not vote, so they go straight to the results view
  // rather than being shown a ballot that would be rejected.
  const mayVote = canVoteInPolls(getToken("roles"));
  const showResults = poll.myVote != null || poll.closed || !mayVote;

  const handleVote = async (optionId) => {
    if (isLoading) return;
    setPendingId(optionId);
    try {
      const result = await votePoll({ postId: post.id, optionId }).unwrap();
      onVoted(post.id, mergePollVote(poll, result?.poll));
    } catch (error) {
      // A closed poll is not a failed vote - the poll genuinely ended. Flip to
      // results rather than leaving the ballot up for a second attempt.
      if (error?.status === 409) {
        onVoted(post.id, { ...poll, closed: true });
        toast.info(error?.data?.message || "This poll has closed");
      } else {
        toast.error(error?.data?.message || "Could not record your vote");
      }
    } finally {
      setPendingId(null);
    }
  };

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <PostHeader
        post={post}
        badgeClass={POLL_BADGE_CLASS}
        badgeLabel="Poll"
        title={poll.anonymous ? "Anonymous" : undefined}
        avatarText={poll.anonymous ? "\u{1F464}" : undefined}
      />

      <div className="px-4 pb-4 pt-3">
        <p className="mb-3 text-[15px] font-semibold text-gray-800">
          {poll.question}
        </p>

        {!showResults && (
          <div className="flex flex-col gap-2">
            {poll.options.map((option) => (
              <button
                key={option.optionId}
                type="button"
                disabled={isLoading}
                onClick={() => handleVote(option.optionId)}
                className="flex w-full items-center gap-2 rounded-md border border-gray-300 px-3 py-2.5 text-left text-sm font-medium text-gray-700 hover:border-teal-600 hover:bg-teal-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <span className="flex-1">{option.text}</span>
                {pendingId === option.optionId && (
                  <CircularProgress size={14} />
                )}
              </button>
            ))}
          </div>
        )}

        {showResults && (
          <div className="flex flex-col gap-2">
            {poll.options.map((option) => {
              const percent = pollPercent(option.votes, poll.totalVotes);
              const mine = poll.myVote === option.optionId;
              return (
                <div
                  key={option.optionId}
                  className="relative overflow-hidden rounded-md bg-gray-100 px-3 py-2.5"
                >
                  {/* A 0% option still gets a visible track, never a collapsed
                      bar or a dash - nobody picking it is a real result. */}
                  <div
                    style={{ width: `${Math.max(percent, 1.5)}%` }}
                    className={
                      option.votes === 0
                        ? "absolute inset-y-0 left-0 border-r-2 border-gray-300 bg-gray-50"
                        : "absolute inset-y-0 left-0 border-r-2 border-teal-600 bg-teal-50"
                    }
                  />
                  <div className="relative flex items-center gap-2 text-sm">
                    <span className="flex-1 font-medium text-gray-700">
                      {option.text}
                      {mine && (
                        <span className="ml-2 text-xs font-semibold text-teal-700">
                          &#10003; your vote
                        </span>
                      )}
                    </span>
                    <span className="flex-none text-xs font-semibold tabular-nums text-gray-500">
                      {option.votes} &middot; {percent}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-gray-400">
          <span>
            {poll.totalVotes} {poll.totalVotes === 1 ? "vote" : "votes"}
          </span>
          {formatPollDeadline(poll.expiresAt, poll.closed) && (
            <>
              <span>&middot;</span>
              <span>{formatPollDeadline(poll.expiresAt, poll.closed)}</span>
            </>
          )}
          {/* No who-voted affordance is rendered for an anonymous poll - not
              even for its author. The server sends no identities either. */}
          {poll.anonymous && (
            <span className={ANON_BADGE_CLASS}>Anonymous</span>
          )}
          {!mayVote && !poll.closed && (
            <span className={ANON_BADGE_CLASS}>View only</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default PollCard;
